import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import TopBar from '../../components/TopBar'
import { useAnalytics } from '../../context/AnalyticsContext'
import { useAuth } from '../../context/AuthContext'
import { getOnboarding, setOnboarding } from './onboardingStorage'
import './onboarding.css'

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

// Адреса и домены, которые Supabase часто отклоняет — проверяем сразу на шаге email
const BLOCKED_EMAILS = [
  'test@gmail.com', 'test@test.com', 'test@example.com', 'test@mail.com',
  'admin@gmail.com', 'user@gmail.com', 'demo@gmail.com', 'sample@gmail.com',
  'test@yahoo.com', 'test@outlook.com', 'test@hotmail.com',
]
const BLOCKED_LOCAL_PARTS = /^(test|admin|user|demo|sample|info|support|noreply|no-reply)$/i
const BLOCKED_DOMAINS = /@(test\.com|example\.com|mailinator\.com|tempmail\.|throwaway|guerrilla|10minutemail|fakeinbox)/i

function isEmailLikelyBlocked(email) {
  const normalized = email.trim().toLowerCase()
  if (BLOCKED_EMAILS.includes(normalized)) return true
  const [local, domain] = normalized.split('@')
  if (!local || !domain) return false
  if (BLOCKED_LOCAL_PARTS.test(local) && (domain.includes('gmail') || domain.includes('yahoo') || domain.includes('outlook') || domain === 'test.com')) return true
  if (BLOCKED_DOMAINS.test(normalized)) return true
  return false
}

const OnboardingEmail = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const location = useLocation()
  const { trackEvent } = useAnalytics()
  const { checkEmailExists } = useAuth()
  const saved = useMemo(() => getOnboarding(), [])

  const [email, setEmail] = useState(saved.email || '')
  const [error, setError] = useState('')
  const [checking, setChecking] = useState(false)

  // Ошибка с шага пароля (invalid email, rate limit) — показываем на шаге email
  useEffect(() => {
    const fromState = location.state?.emailError
    if (fromState) {
      setError(fromState)
      navigate(location.pathname, { replace: true, state: {} })
      return
    }
    // Rate limit: показываем и при возврате на шаг email (из localStorage)
    try {
      const until = localStorage.getItem('astromatch_rate_limit_until')
      if (until) {
        const expiry = Number(until)
        if (Date.now() < expiry) {
          setError('Too many signup attempts. Please wait about an hour and try again.')
        } else {
          localStorage.removeItem('astromatch_rate_limit_until')
        }
      }
    } catch (_) {}
  }, [location.state?.emailError, navigate])

  const onBack = () => navigate('/onboarding/photos')

  const onContinue = async () => {
    const normalized = email.trim().toLowerCase()
    if (!normalized) {
      setError('Email is required.')
      return
    }
    if (!emailRegex.test(normalized)) {
      setError('Please enter a valid email.')
      return
    }
    if (isEmailLikelyBlocked(normalized)) {
      setError('This email address cannot be used for signup. Please use a different email (e.g. your personal Gmail or Yahoo).')
      return
    }
    
    setError('')
    setChecking(true)

    try {
      // Сохраняем email в онбординг
      setOnboarding({ email: normalized })

      // Быстрый RPC в Supabase: определяем, есть ли уже такой email
      const { exists } = await checkEmailExists(normalized)

      trackEvent('funnel_email_completed', { step: 9, exists })

      if (exists) {
        // Уже есть аккаунт → сразу на ввод пароля
        navigate('/onboarding/password/existing')
      } else {
        // Новый клиент → создание пароля
        navigate('/onboarding/password/new')
      }
    } catch (err) {
      console.error('Error checking email:', err)
      setError('Error checking email. Please try again.')
    } finally {
      setChecking(false)
    }
  }

  return (
    <div className="onboarding-page">
      <TopBar currentStep={9} totalSteps={10} onBack={onBack} showBack={true} />
      <div className="onboarding-content">
        <h1 className="h1">{t('onboarding.emailEnterEmailToContinue')}</h1>
        <p className="p">
          {t('onboarding.emailSubtitle')}
        </p>

        <div className="field">
          <label className="label">{t('onboarding.emailLabel')}</label>
          <input
            className={`input ${error ? 'error' : ''}`}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
              if (error) setError('')
            }}
            inputMode="email"
            placeholder={t('onboarding.emailPlaceholder')}
            autoFocus
          />
          {error ? <div className="error-text">{error}</div> : null}
        </div>
      </div>

      <button className="cta-fixed" onClick={onContinue} disabled={checking}>
        {checking ? t('common.checking') : t('common.continue')}
      </button>
    </div>
  )
}

export default OnboardingEmail

